import { Injectable } from '@angular/core';
import { Input, BlobSource, UrlSource, ALL_FORMATS } from 'mediabunny';

export interface MediaMetadata {
	durationSeconds?: number;
	width?: number;
	height?: number;
	/** Frame rate (approximate). Computing this requires reading 100 packets via ffprobe-like stats. */
	fps?: number;
}

/**
 * Extracts media metadata (duration, dimensions, fps) in the browser via mediabunny.
 *
 * Replaces backend ffprobe calls for the upload path and enables on-demand
 * metadata probing for external CDN media (via HTTP range requests — no full
 * download).
 *
 * - `probeFile(file)` — probe a local File blob before upload
 * - `probeUrl(url)` — probe a remote URL (uses HTTP range requests, not full download)
 *
 * Both methods:
 * - Return `MediaMetadata` with fields set only when available
 * - Resolve dimensions + duration cheaply from the container header
 * - Compute fps from packet stats only when `withFps: true` is passed (expensive)
 * - Never throw — probe failures return an empty object so callers stay resilient
 */
@Injectable({ providedIn: 'root' })
export class EditorMediaProbeService {
	async probeFile(file: File, opts?: { withFps?: boolean }): Promise<MediaMetadata> {
		return this.probeInput(new BlobSource(file), opts?.withFps ?? false);
	}

	async probeUrl(url: string, opts?: { withFps?: boolean }): Promise<MediaMetadata> {
		return this.probeInput(new UrlSource(url), opts?.withFps ?? false);
	}

	private async probeInput(source: BlobSource | UrlSource, withFps: boolean): Promise<MediaMetadata> {
		try {
			const input = new Input({ source, formats: ALL_FORMATS });
			const result: MediaMetadata = {};

			// Primary video track → dimensions, duration + optional fps.
			//
			// Duration MUST come from the video track, not `input.computeDuration()`
			// (which returns the max across all tracks). When a source video carries
			// an audio track that runs slightly longer than the picture, the whole-
			// file duration is inflated, and `round(durationSeconds * fps)` then
			// yields one (or more) extra frames — e.g. an 11-frame clip probed as 12
			// (DEV-658). Deriving duration from the video track alone keeps framesCount
			// aligned with the actual number of picture frames.
			let videoTrack = null;
			try {
				videoTrack = await input.getPrimaryVideoTrack();
			} catch { /* no video track — likely audio-only */ }

			if (videoTrack) {
				result.width = videoTrack.displayWidth;
				result.height = videoTrack.displayHeight;
				try {
					const duration = await videoTrack.computeDuration();
					if (Number.isFinite(duration) && duration > 0) result.durationSeconds = duration;
				} catch { /* no video duration */ }
				if (withFps) {
					try {
						const stats = await videoTrack.computePacketStats(100);
						if (stats?.averagePacketRate && Number.isFinite(stats.averagePacketRate)) {
							result.fps = stats.averagePacketRate;
						}
					} catch { /* fps computation failed */ }
				}
			}

			// Audio-only sources (or a video track that reports no duration) fall
			// back to the whole-file duration, which for audio-only equals the audio
			// track's duration.
			if (result.durationSeconds == null) {
				try {
					const duration = await input.computeDuration();
					if (Number.isFinite(duration) && duration > 0) result.durationSeconds = duration;
				} catch { /* no duration */ }
			}

			return result;
		} catch {
			return {};
		}
	}
}
